<template>
  <div class="flex h-full min-h-0 w-full max-w-[384px] flex-1 flex-col">
    <div class="mb-6 flex min-h-0 flex-1 flex-col rounded-[20px] bg-[var(--plugin-gray-100)] p-[3.5rem] lg:mb-8">
      <template v-for="(step, index) in visibleSteps" :key="step.key">
        <div class="flex items-start gap-4 py-1 first:pt-0 last:pb-0">
          <div
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-[8px] bg-[var(--plugin-bg)] [&_svg]:h-5 [&_svg]:w-5"
            :class="stepStyle(index).iconBox"
          >
            <span :class="stepStyle(index).iconColor" class="flex items-center justify-center [&_svg]:shrink-0">
              <component :is="step.icon" />
            </span>
          </div>
          <div class="min-w-0 flex-1 pt-1">
            <div
              class="text-[15px] font-medium leading-tight"
              :class="stepStyle(index).text"
            >
              {{ step.label }}
            </div>
            <div class="mt-1 text-[13px] leading-snug" :class="stepStyle(index).desc">
              {{ step.desc }}
            </div>
          </div>
          <div
            v-if="isCompleted(index)"
            class="ml-3 flex h-5 w-5 shrink-0 items-center justify-center self-center rounded-full bg-primary"
          >
            <svg class="h-2.5 w-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
        <div
          v-if="index < visibleSteps.length - 1"
          class="shrink-0 my-2 bg-[var(--plugin-gray-400)]"
          :class="{ '!bg-primary': isCompleted(index) }"
          style="margin-left: 1.5rem; width: 1px; height: 24px;"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, h } from 'vue'
import { useWizardState } from '@/composables/useWizardState'
import { t } from '@/i18n'
import type { WizardStep } from '@/composables/useWizardState'
import { createStepperIcons } from '@/components/StepperIcons'

/** Only 5 steps in the sidebar (match Flutter: no Summary, no Payment) */
const fullSidebarStepOrder: WizardStep[] = [
  'branches',
  'products',
  'datetime',
  'employees',
  'customer',
]

const stepperIcons = createStepperIcons(h)

const props = defineProps<{ allStepsCompleted?: boolean }>()
const state = useWizardState()

const isWorkshop = computed(
  () =>
    state.product?.type === 'workshop' ||
    (state.product as { is_workshop?: boolean } | null)?.is_workshop === true
)

const sidebarStepOrder = computed(() =>
  isWorkshop.value
    ? fullSidebarStepOrder.filter((s) => s !== 'datetime' && s !== 'employees')
    : fullSidebarStepOrder
)

const stepConfig: Record<WizardStep, { labelKey: string; descKey: string; icon: () => ReturnType<typeof h> }> = {
  branches: { labelKey: 'plugin.steps.branches', descKey: 'plugin.steps.branchesDesc', icon: stepperIcons.branch },
  products: { labelKey: 'plugin.steps.products', descKey: 'plugin.steps.productsDesc', icon: stepperIcons.service },
  datetime: { labelKey: 'plugin.steps.datetime', descKey: 'plugin.steps.datetimeDesc', icon: stepperIcons.date },
  employees: { labelKey: 'plugin.steps.employees', descKey: 'plugin.steps.employeesDesc', icon: stepperIcons.employees },
  customer: { labelKey: 'plugin.steps.customer', descKey: 'plugin.steps.customerDesc', icon: stepperIcons.personalData },
  summary: { labelKey: 'plugin.steps.summary', descKey: 'plugin.steps.summaryDesc', icon: stepperIcons.personalData },
  payment: { labelKey: 'plugin.steps.payment', descKey: 'plugin.steps.paymentDesc', icon: stepperIcons.service },
}

const visibleSteps = computed(() =>
  sidebarStepOrder.value.map((key) => ({
    key,
    label: t(stepConfig[key].labelKey),
    desc: t(stepConfig[key].descKey),
    icon: stepConfig[key].icon,
  }))
)

function currentIndex() {
  const order = sidebarStepOrder.value
  const idx = order.indexOf(state.step)
  if (idx >= 0) return idx
  if (state.step === 'summary' || state.step === 'payment') return order.length
  return 0
}

function isCompleted(index: number) {
  if (props.allStepsCompleted) return true
  const cur = currentIndex()
  return index < cur || state.submitted
}

function stepStyle(index: number) {
  if (props.allStepsCompleted) {
    return {
      iconBox: '',
      iconColor: 'text-[var(--plugin-gray-500)]',
      text: 'text-[var(--plugin-gray-500)]',
      desc: 'text-[var(--plugin-gray-400)]',
    }
  }
  const cur = currentIndex()
  const active = index === cur && !state.submitted
  const completed = isCompleted(index)
  if (active) {
    return {
      iconBox: '',
      iconColor: 'text-primary',
      text: 'text-primary',
      desc: 'text-[var(--plugin-gray-400)]',
    }
  }
  if (completed) {
    return {
      iconBox: '',
      iconColor: 'text-[var(--plugin-gray-500)]',
      text: 'text-[var(--plugin-gray-500)]',
      desc: 'text-[var(--plugin-gray-400)]',
    }
  }
  return {
    iconBox: '',
    iconColor: 'text-[var(--plugin-gray-500)]',
    text: 'text-[var(--plugin-gray-500)]',
    desc: 'text-[var(--plugin-gray-400)]',
  }
}
</script>
